/*
All material copyright ESRI, All Rights Reserved, unless otherwise specified.
See https://js.arcgis.com/4.28/esri/copyright.txt for details.
*/
import e from "../../../Color.js";
import { isSymbol3D as l } from "../../../symbols.js";
import { round as t, numDigits as n, percentChange as i } from "../../../renderers/support/numberUtils.js";
import { applyCIMSymbolColor as o } from "../../../symbols/support/cimSymbolUtils.js";
import { isVolumetricSymbol as s, getSymbolOutlineSize as r } from "../../../symbols/support/utils.js";
import { getSymbolForFlowRenderer as a, createStopLabel as u } from "./utils.js";
import "../../support/widgetUtils.js";
import { isDarkTheme as c } from "../../../support/themeUtils.js";
import m from "../../../symbols/SimpleMarkerSymbol.js";
import f from "../../../symbols/SimpleLineSymbol.js";
const p = 30,
  y = 12,
  b = [255, 255, 255],
  h = [200, 200, 200],
  d = [128, 128, 128],
  w = 20,
  g = 5;
function S(e) {
  return "esri.symbols.SimpleMarkerSymbol" === e.declaredClass;
}
function v(e) {
  return "esri.symbols.PictureMarkerSymbol" === e.declaredClass;
}
function z(e) {
  return "esri.symbols.SimpleLineSymbol" === e.declaredClass;
}
function j(e) {
  return "esri.symbols.TextSymbol" === e.declaredClass;
}
function V(e, l) {
  const t = e.length - 1;
  return e.map((e, n) => u(e, n, t, l));
}
async function k(e, l, n, i, o, a, u) {
  const c = l.legendOptions,
    m = c?.customValues,
    f = u || (await U(e, n)),
    p = l.stops,
    y = !!f,
    b = !!m,
    h = null != l.minSize && null != l.maxSize,
    d = p && p.length > 1,
    w = !!l.target;
  if (!y || !b && !(h || d && !w)) return;
  const g = s(f);
  let S = !1,
    v = null,
    z = null;
  v = g && !d ? t([l.minDataValue, l.maxDataValue]) : m ?? (await D(l, f, i, o?.type));
  const j = e?.authoringInfo,
    k = "univariate-color-size" === j?.type,
    C = k && "above-and-below" === j?.univariateTheme;
  if (!v && d && (v = p.map(e => e.value), S = p.some(e => !!e.label), "flow" === e.type && (v = t(v)), S && (z = p.map(e => e.label))), g && null != v && v?.length > 2 && !C && (v = [v[0], v[v.length - 1]]), !v) return null;
  k && 5 !== v?.length && (v = E({
    minSize: v[0],
    maxSize: v[v.length - 1]
  }));
  const I = g ? x(e, v) : null,
    M = r(f),
    T = S ? null : V(v, a);
  return (await Promise.all(v.map(async (t, n) => {
    const s = g ? I[n] : await B(l, f, t, i, o?.type);
    return {
      value: t,
      symbol: P(C && "class-breaks" === e.type ? L(e, n) : f, s),
      label: S ? z[n] : T[n],
      size: s,
      outlineSize: M
    };
  }))).reverse();
}
function x(e, l) {
  const t = e?.authoringInfo,
    n = "univariate-color-size" === t?.type;
  let i = [y, p];
  if (n) {
    const e = l[0],
      t = l[l.length - 1],
      n = y,
      o = p;
    i = l.map(l => n + (l - e) / (t - e) * (o - n));
  }
  return n && "below" === t?.univariateTheme && i.reverse(), i;
}
function L(e, l) {
  const t = e.classBreakInfos,
    n = t.length,
    i = n < 2 || !(l >= 2) ? t[0].symbol.clone() : t[n - 1].symbol.clone();
  return e.visualVariables.some(e => "color" === e.type) && (i.type.includes("3d") ? I(i) : M(i)), i;
}
async function U(e, l) {
  if ("flow" === e.type) return a(e, l);
  if ("pie-chart" === e.type) return new m({
    color: null,
    outline: e.outline?.width ? e.outline : new f()
  });
  let t = null,
    n = null;
  if ("simple" === e.type) t = e.symbol;else if ("class-breaks" === e.type) {
    const l = e.classBreakInfos;
    t = l && l[0] && l[0].symbol, n = l.length > 1;
  } else if ("unique-value" === e.type) {
    const l = e.uniqueValueInfos;
    t = l?.[0]?.symbol, n = null != l && l.length > 1;
  }
  return !t || C(t) ? null : (t = t.clone(), (l || n) && (t.type.includes("3d") ? I(t) : M(t)), t);
}
function C(e) {
  if (e) {
    if (l(e)) {
      return !!e.symbolLayers && e.symbolLayers.some(e => e && "fill" === e.type);
    }
    return e.type.includes("fill");
  }
  return !1;
}
function I(e) {
  "line-3d" === e.type ? e.symbolLayers.forEach(e => {
    e.material = {
      color: d
    };
  }) : e.symbolLayers.forEach(e => {
    "icon" !== e.type || e.resource?.href ? e.material = {
      color: h
    } : (e.material = {
      color: b
    }, e.outline = {
      color: d,
      size: 1.5
    });
  });
}
function M(l) {
  const t = c();
  if ("cim" === l.type) o(l, new e(h));else if (l.type.includes("line")) l.color = d;else if (l.color = t ? d : b, "simple-marker" === l.type) if (l.outline) {
    const e = l.outline?.color?.toHex();
    "#ffffff" === e && (l.outline.color = d);
  } else l.outline = {
    color: d,
    width: 1.5
  };
}
async function D(e, l, n, i) {
  const o = (await import("../../../renderers/visualVariables/support/visualVariableUtils.js")).getSizeRangeAtScale(e, n, i),
    s = o && E(o);
  if (!o || !s) return;
  let r = s.map(l => T(l, e, o));
  r = t(r);
  for (let t = 1; t < r.length - 1; t++) {
    const o = await q(e, l, r[t], r[t - 1], n, i);
    o && (r[t] = o[0], s[t] = o[1]);
  }
  return r;
}
function E(e) {
  const l = e.minSize,
    t = e.maxSize,
    n = g,
    i = (t - l) / (n - 1),
    o = [];
  for (let s = 0; s < n; s++) o.push(l + i * s);
  return o;
}
function T(e, l, t) {
  const n = t.minSize,
    i = t.maxSize,
    o = l.minDataValue,
    s = l.maxDataValue;
  let r;
  if (e <= n) r = o;else if (e >= i) r = s;else {
    r = (e - n) / (i - n) * (s - o) + o;
  }
  return r;
}
async function q(e, l, o, s, r, a) {
  const u = await B(e, l, o, r, a),
    c = await B(e, l, s, r, a),
    m = n(o),
    f = m.fractional,
    p = w;
  let y = m.integer,
    b = null,
    h = null;
  o > 0 && o < 1 && (b = 10 ** f, y = n(o *= b).integer);
  for (let n = y - 1; n >= 0; n--) {
    const s = 10 ** n;
    let m = Math.floor(o / s) * s,
      f = Math.ceil(o / s) * s;
    null != b && (m /= b, f /= b);
    let y = (m + f) / 2;
    [, y] = t([m, y, f], {
      indexes: [1]
    });
    const d = await B(e, l, m, r, a),
      w = await B(e, l, f, r, a),
      g = await B(e, l, y, r, a),
      S = i(u, d, c, null),
      v = i(u, w, c, null),
      z = i(u, g, c, null);
    let j = S.previous <= p,
      V = v.previous <= p;
    if (j && V && (S.previous <= v.previous ? (j = !0, V = !1) : (V = !0, j = !1)), j ? h = [m, d] : V ? h = [f, w] : z.previous <= p && (h = [y, g]), h) break;
  }
  return h;
}
async function B(e, l, t, n, i) {
  const {
    getSize: o
  } = await import("../../../renderers/visualVariables/support/visualVariableUtils.js");
  return o(e, t, {
    scale: n,
    view: i,
    shape: "simple-marker" === l.type ? l.style : null
  });
}
function P(e, t) {
  const n = e.clone();
  if (l(n)) s(n) || n.symbolLayers.forEach(e => {
    "fill" !== e.type && (e.size = t);
  });else if (S(n)) n.size = t;else if (v(n)) {
    const e = n.width,
      l = n.height;
    n.height = t, n.width = t * (e / l);
  } else z(n) ? n.width = t : j(n) && n.font && (n.font.size = t);
  return n;
}
export { k as getRampStops, p as realWorldMaxSize, y as realWorldMinSize };